import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { useNavState } from '@/providers/NavStateProvider';
import { useEffect, useState } from 'react';
import { useUpdateBusinessPartner } from '@/hooks/api/0460_update_business_partner';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { useLoading } from '@/providers/LoadingProvider';
import { useInvalidateBusinessPartners } from '@/hooks/api/0430_business_partners';
import { useBusinessPartnerContexts } from '@/hooks/api/0440_business_partner_contexts';
import { BusinessPartner } from '@/hooks/api/models';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toastApiError, toastError, toastSuccess } from '@/lib/toast';
import { useBusinessPartner } from '@/hooks/api/0461_business_partner';
import { ScrollTop } from '@/components/element/ScrollTop';
import { MenuButtonGroup } from '@/components/features/SettingMenuButtonGroup';
import { useFindCorporateName } from '@/hooks/api/2270_find_houjin_name';
import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { BUSINESS_PARTNER_DISPLAY_TYPES } from '@/lib/constants';

type FormInputs = {
  identifier: string;
  id: number;
  name: string;
  display_name: string;
  description: string;
  business_partner_context_id: number;
  invalidity: boolean;
  corporate_number: string;
  display_type: 'both' | 'sales' | 'purchase';
};

export const WorkspaceBusinessPartnersEdit = () => {
  const { setTitle, setBreadcrumbs } = useNavState();
  useEffect(() => setTitle('取引先編集'), []);
  useEffect(
    () => setBreadcrumbs([{ label: `取引先設定`, path: `/w/${_w_!.identifier}/setting/business_partners` }]),
    []
  );

  const navigate = useNavigate();
  const { _w_, me } = useCurrentWorkspace();
  const { id: business_partner_id } = useParams();
  const {
    data: _business_partner,
    isSuccess,
    isError,
  } = useBusinessPartner({
    identifier: _w_!.identifier,
    id: Number(business_partner_id),
  });
  const [business_partner, setBusinessPartner] = useState<BusinessPartner>();
  const { setShowLoading } = useLoading();
  const { mutate, isPending, data, error } = useUpdateBusinessPartner();
  const { invalidate } = useInvalidateBusinessPartners(_w_!.identifier);
  const { data: businessPartnerContexts } = useBusinessPartnerContexts();

  useEffect(() => {
    if (isSuccess && _business_partner) {
      setBusinessPartner(_business_partner);
    } else {
      if (isError) {
        navigate(`/w/${_w_!.identifier}/setting/business_partners`);
      }
    }
  }, [isSuccess, isError]);

  const [findCorporateNameParams, setFindCorporateNameParams] = useState({
    identifier: _w_!.identifier,
    corporate_number: '',
    enabled: false,
  });
  const {
    data: findCorporateNameResp,
    isLoading: findCorporateNameRespIsLoading,
    error: findCorporateNameError,
    refetch,
  } = useFindCorporateName(findCorporateNameParams);

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
    },
  });

  useEffect(() => {
    if (business_partner && businessPartnerContexts) {
      setValue('id', business_partner.id);
      setValue('name', business_partner.name);
      setValue('display_name', business_partner.display_name);
      setValue('description', business_partner.description || '');
      setValue('business_partner_context_id', business_partner.business_partner_context.id);
      setValue('invalidity', business_partner.invalidity);
      setValue('corporate_number', business_partner.corporate_number || '');
      setValue('display_type', business_partner.display_type);
    }
  }, [business_partner, businessPartnerContexts]);

  useEffect(
    () => setShowLoading(isPending || findCorporateNameRespIsLoading),
    [isPending, findCorporateNameRespIsLoading]
  );
  useEffect(() => toastApiError(findCorporateNameError), [findCorporateNameError]);

  useEffect(() => {
    if (data) {
      toastSuccess('取引先を編集しました');
      invalidate(); // 取引先のキャッシュクリア
      navigate(`/w/${_w_!.identifier}/setting/business_partners`);
    }
  }, [data]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  const onCorporateNumberClick = () => {
    setFindCorporateNameParams({ ...findCorporateNameParams, corporate_number: getValues('corporate_number') });
  };

  useEffect(() => {
    if (findCorporateNameParams.corporate_number == null || findCorporateNameParams.corporate_number.length == 0) {
      return;
    }

    if (findCorporateNameParams.corporate_number.length == 13) {
      refetch();
    } else {
      toastError('法人番号は半角数字13桁で入力してください。');
    }
  }, [findCorporateNameParams]);

  useEffect(() => {
    if (findCorporateNameResp) {
      setValue('name', findCorporateNameResp.name);
      setValue('business_partner_context_id', findCorporateNameResp.business_partner_context.id);
    }
  }, [findCorporateNameResp]);

  return (
    <WorkspaceLayout>
      <ScrollTop />
      <div className="container-fluid py-4">
        <MenuButtonGroup selected={'business_partner'} />

        <div className="row">
          <div className="col-12 col-lg-9 mx-auto">
            <div className="card mb-4" id="basic-info">
              <div className="card-header d-flex align-items-center">
                <div className="col-md-8">
                  <h5 className="mb-0">取引先編集</h5>
                </div>
              </div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="card-body pt-0">
                  <ApiErrorMessages error={error} />

                  <div className="form-group">
                    <label>法人番号</label>
                    <div className="d-flex align-items-center">
                      <input
                        type="text"
                        maxLength={13}
                        className="form-control flex-1"
                        placeholder={'法人番号'}
                        {...register('corporate_number')}
                      />
                      <button
                        type="button"
                        className="btn btn-icon bg-gradient-secondary ms-1"
                        onClick={onCorporateNumberClick}
                      >
                        法人番号から取得
                      </button>
                    </div>
                    <div className="text-xs text-muted mt-1">
                      半角数字13桁で入力してください。
                      <Link
                        to="https://www.houjin-bangou.nta.go.jp/"
                        className="text-decoration-underline"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        法人番号公表サイト
                      </Link>
                      で検索することができます。
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      正式名称<span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      maxLength={50}
                      className={`form-control ${errors.name && 'is-invalid'}`}
                      placeholder={'正式名称'}
                      {...register('name', {
                        required: '入力して下さい。',
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>表示名</label>
                    <input
                      type="text"
                      maxLength={50}
                      className={`form-control ${errors.display_name && 'is-invalid'}`}
                      placeholder={'表示名'}
                      {...register('display_name')}
                    />
                  </div>
                  <div className="form-group">
                    <label>メモ</label>
                    <textarea
                      rows={5}
                      className={`form-control ${errors.description && 'is-invalid'}`}
                      {...register('description')}
                    ></textarea>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label>
                        事業者種別<span className="text-danger">*</span>
                      </label>
                      <select className="form-control" required={true} {...register('business_partner_context_id')}>
                        {businessPartnerContexts &&
                          businessPartnerContexts.map((c) => (
                            <option key={`context-${c.id}`} value={c.id}>
                              {c.label}
                            </option>
                          ))}
                      </select>
                      <div className="text-xs text-muted mt-1">変更しても過去の支払は影響を受けません</div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label>
                        表示設定<span className="text-danger">*</span>
                      </label>
                      <select className="form-control" required={true} {...register('display_type')}>
                        {['both', 'sales', 'purchase'].map((t) => (
                          <option key={`display-type-${t}`} value={t}>
                            {BUSINESS_PARTNER_DISPLAY_TYPES[t]}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>無効化</label>
                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        id="on-invalidity"
                        className="form-check-input"
                        {...register('invalidity')}
                      />
                      <label className="form-check-label" htmlFor="on-invalidity">
                        入力時の選択候補から除外されます
                      </label>
                    </div>
                  </div>
                </div>
                <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                  <Link
                    to={`/w/${_w_!.identifier}/setting/business_partners`}
                    className="btn btn-outline-secondary me-2"
                  >
                    戻る
                  </Link>
                  <button className="btn bg-gradient-dark btn-icon">
                    <i className="fas fa-check-circle"></i>変更を確定する
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};
